import { Proto } from "./Proto";
import ISODateString = Proto.ISODateString;

/**
 * Waitlist entry representing a reservation's position in a queue for a location and time period.
 *
 * Supports two modes for specifying the blocking reservation:
 * 1. Direct mode: Provide blockingRsrvId directly
 * 2. Event-based mode: Provide blockingEventId + conflictStartDt + conflictEndDt
 *    Backend will resolve to blockingRsrvId by matching event_id + space + time overlap
 */

export interface Waitlists {
    waitlists: WaitlistEntry[];
    errors?: WaitlistErrorResponse[];
}

export interface WaitlistErrorResponse {
    message: string;
    error: string;
    details?: string;
    status?: number;
}

export interface WaitlistEntry {
    waitlistId: number | null;
    groupKey: string;
    spaceId: number;
    spaceName?: string | null;
    blockingRsrvId: number;
    blockingEventId: number;
    blockingEventName: string;
    waiting: WaitingEntry[];
    informal?: boolean;
}

/** POST /waitlist/event.json — body for batch fetching waitlist entries by multiple event IDs (plus-separated). */
export interface WaitlistPostGetEventBody {
    event_id: string;
    include_requests?: WaitlistRequestFilter;
}

/** POST /waitlist/list.json body root (matches Java @JsonRootName("request")). */
export interface WaitlistCreatRequest {
    request: {
        waitlists: WaitlistRequest[];
    };
}

/**
 * POST /waitlist/list.json?request_method=get — JSON body (matches Java {@code WaitlistPostGetDoc}).
 * Use when {@code space_id} (plus-separated list) or the full query is too long for a GET URL.
 */
export interface WaitlistPostGetListBody {
    waitlist_id?: number | null;
    /** Single id or '+'-separated ids (e.g. {@code "101+102"}). */
    space_id?: string | null;
    rsrv_id?: number | null;
    blocking_rsrv_id?: number | null;
    start_dt?: ISODateString | string | Date | null;
    end_dt?: ISODateString | string | Date | null;
    include_requests?: WaitlistRequestFilter;
}

/**
 * GET /waitlist/list.json — params must satisfy the API: either (space_id + start_dt + end_dt)
 * or rsrv_id, plus optional filters. Use {@link WaitlistEventQueryParams} for GET /waitlist/event.json.
 */
export interface WaitlistListQueryParams {
    waitlist_id?: number;
    space_id?: number;
    rsrv_id?: number;
    blocking_rsrv_id?: number;
    start_dt?: ISODateString | string | Date;
    end_dt?: ISODateString | string | Date;
    include_requests?: WaitlistRequestFilter;
}

/** GET /waitlist/event.json */
export interface WaitlistEventQueryParams {
    event_id: number;
    space_id?: number;
    include_requests?: WaitlistRequestFilter;
}

/** Union of store fetch shapes */
export type WaitlistQueryParams = WaitlistListQueryParams | WaitlistEventQueryParams;

export type WaitlistUrlParams = Record<string, string | number | (string | number)[] | null | undefined> & {
    waitlist_id?: number;
    space_id?: number;
    rsrv_id?: number;
    blocking_rsrv_id?: number;
    event_id?: number;
    start_dt?: string;
    end_dt?: string;
    include_requests?: string;
};

/** POST /waitlist/reorder.json — waiting row (Java WaitlistController reorder waiting item). */
export interface WaitlistReorderWaitingEntry {
    rsrvId: number;
    notifyId: number | null;
}

/** POST /waitlist/reorder.json — inner waitlist item (Java WaitlistController.WaitlistReorderRequest). */
export interface WaitlistReorderRequest {
    waitlistId: number;
    blockingEventId?: number;
    blockingRsrvId?: number;
    waiting: WaitlistReorderWaitingEntry[];
}

/** POST body root @JsonRootName("reorder") */
export interface WaitlistsReorderRequest {
    reorder: {
        waitlists: WaitlistReorderRequest[];
    };
}

export type WaitlistQueryKey = "space_id" | "event_id" | "rsrv_id";

export type WaitlistQuery =
    | { type: "space_id"; spaceId: number }
    | { type: "event_id"; eventId: number }
    | { type: "rsrv_id"; rsrvId: number };

export interface WaitingEntry {
    rsrvId: number;
    startDt: ISODateString | string | Date;
    endDt: ISODateString | string | Date;
    position: number;
    eventId: number;
    eventName: string;
    isRequest: boolean;
    notifyId: number;
    spaceId?: number;
    eventLocator?: string;
}

export interface WaitlistRequest {
    spaceId: number;
    blockingRsrvId?: number;
    blockingEventId?: number;
    waiting: WaitingEntryRequest[];
}

export interface WaitingEntryRequest {
    rsrvId: number;
    position?: number;
    conflictStartDt?: ISODateString | string | Date;
    conflictEndDt?: ISODateString | string | Date;
    notifyId?: number;
}

export type WaitlistType = "request" | "assign" | null;

export const WaitlistType = {
    Request: "request",
    Assign: "assign",
} as const;

export type WaitlistRequestFilter = "formal" | "with_competing" | "all_competes";

export namespace Waitlist {
    export const WaitlistLevels = [
        { itemId: "", itemName: "Do Not Allow", telemetryLabel: "DoNotAllow" },
        { itemId: "request", itemName: "Requests Only", telemetryLabel: "RequestsOnly" },
        { itemId: "assign", itemName: "Requests and Assignments", telemetryLabel: "RequestsAndAssignments" },
    ];
}
